import React, { useContext, useEffect } from "react";
import { Redirect, Switch, useLocation, useHistory } from "react-router-dom";
import { Alert } from "./components/common/Alert";
import SignIn from "./components/SignIn";
import PrivacyPolicy from "./components/PrivacyPolicy";
import Spinner from "./components/common/spinner/Spinner";
import { PrivateRoute, PublicRoute } from "./components/common/PrivateRoute";
import Header from "./components/common/Header";
import { AppContext } from "./AppContext";
import { Scrollbars } from "react-custom-scrollbars";
import Sidebar from "./components/common/Sidebar";
import Dashboard from "./components/dashboard/Dashboard";
import ReportManagement from "./components/reportManagement/ReportManagement";
import FormManagement from "./components/formManagement/FormManagement";
import StoreManagement from "./components/storeManagement/StoreManagement";
import OrgManagement from "./components/orgManagement/OrgManagement";
import SignUp from "./components/SignUp";
import Profile from "./components/profile/Profile";
import ResetPassword from "./components/ResetPassword";
import { useTranslation } from "react-i18next";

// 개발 모드에서만 디버그 도구 로드
// import "./utils/debugUtils";
// import OfflineDebugPanel from "./components/debug/OfflineDebugPanel";

// 백그라운드 동기화 시스템 초기화 및 전역 토큰 설정
import "./utils/backgroundSync";
import { setAuthToken, getPendingReportsCount, startManualSync } from "./utils/backgroundSync";
import { measureNetworkSpeed } from "./utils/speedTest";
import OfflineIndicator from "./components/offline/OfflineIndicator";
import { setupAxiosInterceptors } from "./utils/axiosInterceptors";

function App() {
  const { auth, setAuth } = useContext(AppContext);
  const location = useLocation(); // 페이지 변경 감지
  const history = useHistory();
  const { t } = useTranslation();

  // Axios 인터셉터 설정 (앱 시작 시 한 번만)
  useEffect(() => {
    setupAxiosInterceptors(setAuth, history, t);
  }, [setAuth, history, t]);

  // 전역 인증 토큰 설정 - 모든 페이지에서 동기화 가능
  useEffect(() => {
    if (auth && auth.token) {
      // console.log('[App] 전역 인증 토큰 설정:', auth.token ? '토큰 설정됨' : '토큰 없음');
      setAuthToken(auth.token);
    } else {
      // console.log('[App] 인증 토큰 없음 또는 로그아웃');
      setAuthToken(null);
    }
  }, [auth]);

  // 🆕 페이지 변경 시마다 네트워크 속도 체크 및 자동 동기화
  useEffect(() => {
    // 로그인되어 있지 않으면 스킵
    if (!auth || !auth.token) {
      return;
    }

    // console.log('[App] 📍 페이지 변경 감지:', location.pathname);

    // 비동기 함수로 네트워크 체크 및 동기화
    const checkAndSync = async () => {
      try {
        // 1. 대기 중인 오프라인 레포트 확인
        const pendingCount = await getPendingReportsCount();

        if (pendingCount === 0) {
          // console.log('[App] ℹ️ 동기화할 오프라인 레포트 없음');
          return;
        }

        // console.log(`[App] 📦 ${pendingCount}개의 오프라인 레포트 대기 중`);

        // 2. 네트워크 속도 측정
        // console.log('[App] 🌐 네트워크 속도 측정 중...');
        const speed = await measureNetworkSpeed(true); // 캐시 무시하고 측정

        // console.log(`[App] 📊 네트워크 속도: ${speed}`);

        // 3. 속도가 'fast'이면 자동 동기화 시작
        if (speed === 'fast') {
          // console.log('[App] 🚀 네트워크 빠름 감지, 수동 동기화 트리거...');

          // 🔥 수동 동기화 시작
          await startManualSync();

          // console.log('[App] ✅ 동기화 완료 또는 진행 중');
        } else {
          // console.log(`[App] ⏸️ 네트워크 느림 (${speed}), 동기화 대기 중...`);
          // console.log('[App] 💡 네트워크가 개선되면 자동으로 동기화됩니다.');
        }

      } catch (error) {
        // console.error('[App] ❌ 자동 동기화 체크 실패:', error);
      }
    };

    // 페이지 로드 후 1초 뒤에 실행 (초기 렌더링 완료 대기)
    const timer = setTimeout(() => {
      checkAndSync();
    }, 1000);

    return () => clearTimeout(timer);
  }, [location.pathname, auth]); // 페이지 경로 변경 또는 인증 상태 변경 시 실행
  return (
    <Scrollbars
      style={{ width: "100%", height: "100vh" }}
      renderThumbVertical={(props) => <div {...props} className="scroll-thumb-vertical black" />}
      renderThumbHorizontal={(props) => <div {...props} className="scroll-thumb-horizontal black" />}
      autoHide
    >
      {auth && auth.token && auth.user && <Sidebar />}
      <div className="main-content">
        {auth && auth.token && auth.user && <Header />}
        <div className="container-fluid mt-5">
          <Switch>
            <PrivateRoute exact path="/dashboard" component={Dashboard} />
            <PrivateRoute path="/orgManagement" component={OrgManagement} />
            <PrivateRoute path="/storeManagement" component={StoreManagement} />
            <PrivateRoute path="/reportManagement" component={ReportManagement} />
            <PrivateRoute path="/formManagement" component={FormManagement} />
            {/* 20221026KSW */}
            <PrivateRoute path="/profile" component={Profile} />
            <PublicRoute exact path="/resetPassword/:token" component={ResetPassword} />
            <PublicRoute exact path="/signUp/:token" component={SignUp} />
            <PublicRoute exact path="/signIn" component={SignIn} />
            <PublicRoute exact path="/privacyPolicy" component={PrivacyPolicy} />
            <Redirect to="/dashboard" />
          </Switch>
        </div>
      </div>
      <Alert />
      <Spinner />
      {/* 오프라인 상태 표시 (항상 표시, 데이터 없을 때는 회색) */}
      <OfflineIndicator />
      {/* 디버그 패널 표시 (실서버 배포시 주석처리) */}
      {/* <OfflineDebugPanel /> */}
    </Scrollbars>
  );
}

export default App;
